const ItemType = require('@/lib/Items/ItemType');
const { DEFAULT_ITEM_TYPE_ICON, ItemTypeIcons } = require('@/lib/Items/ItemTypeIcons');
const { toUpperSnakeCase } = require('@/functions/strings');

/**
 * @typedef {Object} ItemTypeTabConfig
 * @property {string} itemType
 * @property {boolean} sellable - Whether this item type can be sold directly from the Sell
 *   screen (and therefore needs a tab there). Some item types (e.g. Bundle, Surcharge) can
 *   still appear as an Order Details tab even though they're never sold directly.
 */

/**
 * Single source of truth for the order and lang key of every item type that can be shown as a
 * tab, either on the Sell screen (ItemPanelTabs) or the Order Details screen (OrderDetailsTabs).
 *
 * Icons live in ItemTypeIcons, as they're used in places other than tabs.
 *
 * IMPORTANT: this array's order IS the canonical tab order. Both ItemPanelTabs and
 * OrderDetailsTabs should end up showing tabs in this same relative order - do not
 * re-sort by anything other than this (e.g. alphabetically) without updating this list.
 *
 * @type {ItemTypeTabConfig[]}
 */
const ITEM_TYPE_TAB_ORDER = [
    { itemType: ItemType.TICKET, sellable: true },
    { itemType: ItemType.PRODUCT, sellable: true },
    { itemType: ItemType.PRODUCT_FOOD, sellable: true },
    { itemType: ItemType.MEMBERSHIP_PLAN, sellable: true },
    { itemType: ItemType.GIFT_VOUCHER, sellable: true },

    // The following item types can never be sold directly from the Sell screen, so they don't
    // get a tab there - but they can still appear as their own tab on Order Details, so they
    // still need a canonical position/lang key.
    { itemType: ItemType.BUNDLE, sellable: false },
    { itemType: ItemType.DELIVERY_METHOD, sellable: false },
    { itemType: ItemType.SURCHARGE, sellable: false },
    { itemType: ItemType.GENERIC, sellable: false },
];

/**
 * Get the lang key for an item type's tab label.
 * e.g. 'Ticket' -> 'ITEMS_TAB_NAME.TICKET'
 *
 * @param {string} itemType
 *
 * @return {string}
 */
const getItemTypeTabLangKey = (itemType) => 'ITEMS_TAB_NAME.' + toUpperSnakeCase(itemType);

/**
 * Get the lang key for a count of items of this type, e.g. "2 Tickets".
 * e.g. 'Ticket' -> 'ITEM_TYPE_COUNT.TICKET'
 *
 * @param {string} itemType
 *
 * @return {string}
 */
const getItemTypeCountLangKey = (itemType) => 'ITEM_TYPE_COUNT.' + toUpperSnakeCase(itemType);

/**
 * Get the icon for an item type's tab, for use on mobile devices.
 *
 * @param {string} itemType
 *
 * @return {string}
 */
const getItemTypeTabIcon = (itemType) => ItemTypeIcons[itemType] ?? DEFAULT_ITEM_TYPE_ICON;

/**
 * Get this item type's position in the canonical tab order (lower = earlier/first).
 * Item types not found in ITEM_TYPE_TAB_ORDER sort after all recognised item types.
 *
 * @param {string} itemType
 *
 * @return {number}
 */
const getItemTypeTabSortIndex = (itemType) => {
    const index = ITEM_TYPE_TAB_ORDER.findIndex((t) => t.itemType === itemType);
    return index === -1 ? ITEM_TYPE_TAB_ORDER.length : index;
};

/**
 * Build the tab config (itemType, icon, labelLangKey) for every item type that can be sold
 * directly from the Sell screen, in canonical order. Used by ItemPanelTabs to build its
 * `allTabs`.
 *
 * @return {{itemType: string, icon: string, labelLangKey: string}[]}
 */
const getSellableItemTypeTabs = () => ITEM_TYPE_TAB_ORDER
    .filter((tab) => tab.sellable)
    .map((tab) => ({
        itemType: tab.itemType,
        icon: getItemTypeTabIcon(tab.itemType),
        labelLangKey: getItemTypeTabLangKey(tab.itemType),
    }));

/**
 * Compare two item types by their canonical tab order, for use in Array#sort.
 * Item types not found in ITEM_TYPE_TAB_ORDER sort after all recognised item types, then
 * alphabetically amongst themselves.
 *
 * @param {string} itemTypeA
 * @param {string} itemTypeB
 *
 * @return {number}
 */
const compareItemTypesByTabOrder = (itemTypeA, itemTypeB) => {
    const diff = getItemTypeTabSortIndex(itemTypeA) - getItemTypeTabSortIndex(itemTypeB);
    if (diff !== 0) {
        return diff;
    }
    // Both unrecognised (or the same type) - fall back to alphabetical so the order is at
    // least stable and predictable.
    return itemTypeA.localeCompare(itemTypeB);
};

module.exports = {
    ITEM_TYPE_TAB_ORDER,
    getItemTypeTabLangKey,
    getItemTypeCountLangKey,
    getItemTypeTabIcon,
    getItemTypeTabSortIndex,
    getSellableItemTypeTabs,
    compareItemTypesByTabOrder,
};
